/**
 * YouTube Service - Simple Video Selection
 * Get top 10 videos and select 3 based on: popularity, concept match, and recency
 */

import { YOUTUBE_SEARCH_ENDPOINT } from '@/config/apiEndpoints';
import { CacheService } from './cacheService';
import { LANGUAGE_CONFIG } from '@/config/languages';

export interface YouTubeVideo {
  id: string;
  title: string;
  channel: string;
  duration?: number;
  viewCount?: number;
  publishedAt?: string;
  description?: string;
}

/**
 * Output interface with 3 selected videos
 */
interface SelectedVideos {
  popular: string; // Highest view count
  conceptual: string; // Best concept match score
  recent: string; // Most recent video
}

/**
 * Calculate concept match score (0-100) - Enhanced SEO-aware scoring
/**
 * Calculate concept match score (0-100) - Enhanced SEO-aware scoring with multiple factors
 * Scoring factors:
 * - Keyword position in title (earlier = better)
 * - Keyword frequency (more mentions = better, with diminishing returns)
 * - Keyword specificity (longer keywords = more specific)
 * - Keyword proximity (keywords close together = better match quality)
 * - Title coverage (all keywords in title = better than spread)
 * - Language variation bonus/penalty (+15% / -10%)
 * 
 * Checks all language variations:
 * - English name (e.g., "Bengali")
 * - Native name (e.g., "বাংলা")
 * - Aliases (e.g., "Bangla", "bengali")
 */
function calculateConceptMatch(title: string, description: string, searchTerm: string, languageCode: string = ''): number {
  if (!searchTerm) return 0;

  const titleLower = title.toLowerCase();
  const descLower = description.toLowerCase();
  const fullText = `${titleLower} ${descLower}`;
  
  // Extract search keywords (remove language suffix)
  const searchQuery = searchTerm.toLowerCase().split(' in ')[0].trim();
  const keywords = searchQuery.split(/\s+/).filter(k => k.length > 0);

  if (keywords.length === 0) return 0;

  let totalScore = 0;
  let keywordScores: Record<string, number> = {};

  // Helper function to escape special regex characters
  const escapeRegex = (str: string) => str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

  for (const keyword of keywords) {
    // Count keyword frequency with diminishing returns
    const escapedKeyword = escapeRegex(keyword);
    const titleMatches = (titleLower.match(new RegExp(escapedKeyword, 'g')) || []).length;
    const descMatches = (descLower.match(new RegExp(escapedKeyword, 'g')) || []).length;
    const totalMatches = titleMatches + descMatches;
    
    // Frequency bonus: 1 match=1x, 2 matches=1.5x, 3+ matches=1.8x (diminishing returns)
    const frequencyMultiplier = Math.min(1 + (Math.log(totalMatches + 1) * 0.3), 1.8);

    // Keyword specificity bonus (longer keywords are more specific)
    const specificityBonus = Math.min(keyword.length / 10, 0.2); // Max 20% bonus for long keywords

    // Position-based scoring
    const titleIndex = titleLower.indexOf(keyword);
    const isEarlyInTitle = titleIndex !== -1 && titleIndex < 30; // First 30 chars

    // Exact word boundary check
    const exactRegex = new RegExp(`\\b${escapedKeyword}\\b`, 'i');
    const exactMatch = exactRegex.test(fullText);

    // Title coverage
    const inTitle = titleLower.includes(keyword);
    const inDescription = descLower.includes(keyword);

    let baseScore = 0;

    if (inTitle && exactMatch && isEarlyInTitle) {
      baseScore = 100; // Exact match early in title (perfect positioning)
    } else if (inTitle && exactMatch) {
      baseScore = 95; // Exact match anywhere in title
    } else if (inTitle) {
      baseScore = 85; // Partial match in title
    } else if (exactMatch) {
      baseScore = 75; // Exact match in description
    } else if (inDescription) {
      baseScore = 55; // Partial match in description
    } else {
      baseScore = 30; // Weak match
    }

    // Apply multipliers
    const adjustedScore = baseScore * frequencyMultiplier * (1 + specificityBonus);
    keywordScores[keyword] = Math.min(adjustedScore, 100);
    totalScore += Math.min(adjustedScore, 100);
  }

  // Average score with bonus for comprehensive title coverage
  let baseScore = Math.round(totalScore / keywords.length);

  // Title comprehensiveness bonus: +10% if ALL keywords are in title
  const allKeywordsInTitle = keywords.every(k => titleLower.includes(k));
  if (allKeywordsInTitle) {
    baseScore = Math.round(baseScore * 1.1);
    console.log(`   ✨ All keywords in title (+10% bonus)`);
  }

  // Keyword proximity bonus: +8% if keywords appear within proximity (same sentence/phrase)
  const titleWords = titleLower.split(/\s+/);
  const keywordPositions = keywords
    .map(k => titleWords.findIndex(w => w.includes(k)))
    .filter(pos => pos !== -1);
  
  if (keywordPositions.length >= 2) {
    const maxDistance = Math.max(...keywordPositions) - Math.min(...keywordPositions);
    if (maxDistance <= 5) {
      // Keywords are close together (within 5 words)
      baseScore = Math.round(baseScore * 1.08);
      console.log(`   🎯 Keywords proximally close (+8% bonus)`);
    }
  }

  // Apply language-based bonus/penalty - check all language variations
  if (languageCode && languageCode.trim().length > 0) {
    const langConfig = LANGUAGE_CONFIG[languageCode.toLowerCase()];
    if (langConfig) {
      // Build array of all language name variations to check
      const languageVariations = [
        langConfig.name.toLowerCase(),           // English name (e.g., "Bengali")
        langConfig.nativeName.toLowerCase(),     // Native name (e.g., "বাংলা")
        ...langConfig.aliases.map(a => a.toLowerCase())  // All aliases (e.g., "bangla", "bengali")
      ];

      // Check if ANY language variation exists in title
      const hasLanguageInTitle = languageVariations.some(variation => titleLower.includes(variation));

      if (hasLanguageInTitle) {
        // +15% bonus if any language variation found in title
        baseScore = Math.round(baseScore * 1.15);
        const matchedVariation = languageVariations.find(v => titleLower.includes(v));
        console.log(`   🌍 Language Bonus: "${matchedVariation}" found in title (+15%)`);
      } else {
        // -10% penalty if NO language variation found in title
        baseScore = Math.round(baseScore * 0.9);
        console.log(`   ⚠️ Language Penalty: ${langConfig.name} not detected in title (-10%)`);
      }
    }
  }

  // Return calculated score (uncapped - allows scores > 100 for highly relevant matches)
  return baseScore;
}

/**
 * Parse ISO 8601 duration to seconds
 */
function parseDurationToSeconds(duration: string | number): number {
  if (typeof duration === 'number') {
    return duration;
  }

  if (typeof duration !== 'string' || !duration) {
    return 0;
  }

  if (duration.startsWith('PT')) {
    const regex = /PT(?:(\d+)H)?(?:(\d+)M)?(?:(\d+)S)?/;
    const matches = duration.match(regex);

    if (!matches) return 0;

    const hours = parseInt(matches[1] || '0', 10);
    const minutes = parseInt(matches[2] || '0', 10);
    const seconds = parseInt(matches[3] || '0', 10);

    return hours * 3600 + minutes * 60 + seconds;
  }

  return 0;
}

/**
 * Validate video length - must be >= 5 minutes
 */
function isValidVideoLength(durationInSeconds: number): boolean {
  const minDurationSeconds = 5 * 60; // 5 minutes
  const maxDurationSeconds = 4 * 60 * 60; // 4 hours
  
  return durationInSeconds >= minDurationSeconds && durationInSeconds <= maxDurationSeconds;
}

/**
 * Check if video is likely music content (to filter out music videos)
 */
function isMusicContent(title: string, description: string, channel: string): boolean {
  const text = `${title} ${description} ${channel}`.toLowerCase();
  
  // Music-related keywords that indicate music videos
  const musicKeywords = [
    'music video', 'official video', 'official audio', 'lyric video',
    'song', 'artist', 'album', 'beat', 'track', 'remix',
    'music production', 'sound track', 'ost', 'original soundtrack',
    'music channel', 'music official', 'audio official',
    'playlist', 'compilation'
  ];
  
  // Check if multiple music keywords appear
  let musicKeywordCount = 0;
  for (const keyword of musicKeywords) {
    if (text.includes(keyword)) {
      musicKeywordCount++;
    }
  }
  
  // If 2+ music keywords found, it's likely a music video
  if (musicKeywordCount >= 2) {
    return true;
  }
  
  // Check channel names known for music
  const musicChannels = [
    'vevo', 'music', 'songs', 'beats', 'audio', 'soundtrack',
    'official', 'label', 'records'
  ];
  
  for (const channelKeyword of musicChannels) {
    if (channel.toLowerCase().includes(channelKeyword)) {
      // Double check if music keywords also present
      if (musicKeywordCount >= 1) {
        return true;
      }
    }
  }
  
  return false;
}

/**
 * Fetch YouTube videos and return 3 selected videos: popular, conceptual, recent
 */
export async function fetchVideoIdForArticle(
  searchTerm: string,
  language: string = 'en',
  topic?: string
): Promise<{ videoId: string | null; videos?: SelectedVideos; error: any }> {
  try {
    if (!searchTerm || searchTerm.trim().length === 0) {
      return { videoId: null, error: 'Empty search term' };
    }

    const cacheKey = CacheService.generateKey('youtube', searchTerm, 1);
    const cached = CacheService.get<string>(cacheKey);

    if (cached) {
      console.log('✅ Using cached video ID for:', searchTerm);
      return { videoId: cached, error: null };
    }

    const langCode = language.substring(0, 2).toLowerCase();
    const regionMap: Record<string, string> = {
      'en': 'US', 'es': 'ES', 'fr': 'FR', 'de': 'DE', 'it': 'IT',
      'pt': 'BR', 'ru': 'RU', 'ja': 'JP', 'ko': 'KR', 'zh': 'CN',
      'ar': 'SA', 'hi': 'IN', 'bn': 'BD', 'tr': 'TR', 'nl': 'NL',
      'pl': 'PL', 'sv': 'SE', 'no': 'NO', 'da': 'DK', 'fi': 'FI'
    };

    const regionCode = regionMap[langCode] || 'US';
    console.log(`🔍 Searching: "${searchTerm}" (Region: ${regionCode})`);

    // Request 200 videos to ensure 30+ remain after filtering (YouTube API has pagination)
    // Backend will make multiple requests if needed to reach 30+ results
    // Use NO duration filter - let YouTube return all results, filter on client-side for 3+ min
    let params = new URLSearchParams({
      q: searchTerm,
      maxResults: '200',
      order: 'relevance',
      type: 'video',
      // Removed videoDuration filter - all results from YouTube, filter client-side
      safeSearch: 'strict',
      relevanceLanguage: langCode,
      regionCode: regionCode,
    });

    console.log('📡 Frontend API Request - youtubeService.ts:', {
      q: searchTerm,
      maxResults: 200,
      videoDuration: 'NONE - filtering client-side for 3+ min videos',
      endpoint: YOUTUBE_SEARCH_ENDPOINT,
      fullUrl: `${YOUTUBE_SEARCH_ENDPOINT}?${params.toString()}`,
    });

    const response = await fetch(`${YOUTUBE_SEARCH_ENDPOINT}?${params.toString()}`);

    if (!response.ok) {
      return { videoId: null, error: `Search failed: ${response.status}` };
    }

    const result = await response.json();

    console.log('🎬 Backend Response received:', {
      success: result.success,
      dataLength: result.data?.length || 0,
      hasData: !!result.data,
      error: result.error,
      message: result.message,
    });

    if (!result.data || result.data.length === 0) {
      console.warn('❌ No videos found for:', searchTerm);
      console.warn('Full result:', result);
      return { videoId: null, error: 'No videos found' };
    }

    console.log(`\n📊 ANALYZING ALL ${result.data.length} VIDEOS FROM API`);
    console.log(`Extracting metadata and filtering for minimum duration (3+ minutes)...\n`);
    
    // Analyze ALL videos and filter for minimum duration
    const validDurationVideos: any[] = [];
    let skippedTooShort = 0;

    for (const video of result.data) {
      const videoId = video.id || video.videoId;
      const title = video.title || '';
      const description = video.description || '';
      const channel = video.channel?.title || video.channelTitle || 'Unknown';
      
      const durationRaw = video.duration || video.contentDetails?.duration || 0;
      const durationInSeconds = parseDurationToSeconds(durationRaw);
      const durationMin = Math.floor(durationInSeconds / 60);
      
      // Filter for minimum duration: 3+ minutes
      if (durationMin < 3) {
        skippedTooShort++;
        continue;
      }
      
      let viewCount = video.viewCount || 0;
      if (typeof viewCount === 'string') {
        viewCount = parseInt(viewCount, 10) || 0;
      }

      const publishedAt = video.publishedAt || new Date().toISOString();
      
      // Get language code for concept scoring bonus/penalty (checks all variations)
      const langCode = language.substring(0, 2).toLowerCase();
      
      const conceptMatchScore = calculateConceptMatch(title, description, searchTerm, langCode);

      validDurationVideos.push({
        videoId,
        title,
        channel,
        duration: durationInSeconds,
        viewCount,
        publishedAt,
        description,
        conceptMatchScore
      });

      console.log(`#${validDurationVideos.length}: "${title.substring(0, 45)}..."`);
      console.log(`   Duration: ${durationMin}m | Views: ${viewCount.toLocaleString()} | Match: ${conceptMatchScore}%`);
    }

    console.log(`\n📈 FILTERING SUMMARY:`);
    console.log(`   Total from API: ${result.data.length}`);
    console.log(`   - Too Short (< 3m): ${skippedTooShort}`);
    console.log(`   ✅ Valid Duration (3+m): ${validDurationVideos.length}\n`);

    if (validDurationVideos.length < 30) {
      console.warn(`⚠️  WARNING: Only ${validDurationVideos.length} videos with 3+ minutes found (goal: 30+)`);
      console.warn('   This may indicate: insufficient results or low relevance match');
    }
    
    if (validDurationVideos.length === 0) {
      console.warn('❌ No videos found with minimum 3 minutes duration');
      return { videoId: null, error: 'No videos found with minimum 3 minutes duration' };
    }

    console.log(`\n🏆 SELECTING 3 BEST VIDEOS FROM ${validDurationVideos.length} VIDEOS (3+M, ENHANCED CONCEPT SCORING):\n`);
    console.log(`═══════════════════════════════════════════════════════════\n`);

    // STEP 1: Rank top 5 videos by concept score
    const topConceptVideos = [...validDurationVideos].sort((a, b) => b.conceptMatchScore - a.conceptMatchScore).slice(0, 5);
    
    console.log(`📊 TOP 5 CONCEPT-MATCHED VIDEOS:`);
    for (let i = 0; i < topConceptVideos.length; i++) {
      const v = topConceptVideos[i];
      console.log(`   ${i + 1}. ${v.conceptMatchScore}% - "${v.title.substring(0, 45)}..."`);
    }
    console.log();

    // STEP 2: Calculate combined score (popularity + concept ratio) for top 5 videos
    // Combined score = (viewCount normalized) * 0.4 + (conceptMatchScore) * 0.6
    const maxViews = Math.max(...topConceptVideos.map(v => v.viewCount || 1));
    const videosWithCombinedScore = topConceptVideos.map(video => {
      const viewsNormalized = maxViews > 0 ? (video.viewCount / maxViews) * 100 : 0;
      const combinedScore = (viewsNormalized * 0.4) + (video.conceptMatchScore * 0.6);
      return { ...video, combinedScore };
    });

    // Sort by combined score and select top 3
    const top3Videos = videosWithCombinedScore
      .sort((a, b) => b.combinedScore - a.combinedScore)
      .slice(0, 3);

    console.log(`\n🏆 SELECTING TOP 3 BY COMBINED SCORE (40% Popularity + 60% Concept):\n`);
    console.log(`═══════════════════════════════════════════════════════════\n`);

    const firstVideo = top3Videos[0];
    console.log(`1️⃣ FIRST: ${firstVideo.combinedScore.toFixed(1)} combined score`);
    console.log(`   Title: "${firstVideo.title.substring(0, 50)}..."`);
    console.log(`   Concept: ${firstVideo.conceptMatchScore}% | Views: ${firstVideo.viewCount.toLocaleString()} | Duration: ${Math.floor(firstVideo.duration / 60)}m`);
    console.log(`   ID: ${firstVideo.videoId}\n`);

    const secondVideo = top3Videos[1];
    console.log(`2️⃣ SECOND: ${secondVideo.combinedScore.toFixed(1)} combined score`);
    console.log(`   Title: "${secondVideo.title.substring(0, 50)}..."`);
    console.log(`   Concept: ${secondVideo.conceptMatchScore}% | Views: ${secondVideo.viewCount.toLocaleString()} | Duration: ${Math.floor(secondVideo.duration / 60)}m`);
    console.log(`   ID: ${secondVideo.videoId}\n`);

    const thirdVideo = top3Videos[2];
    console.log(`3️⃣ THIRD: ${thirdVideo.combinedScore.toFixed(1)} combined score`);
    console.log(`   Title: "${thirdVideo.title.substring(0, 50)}..."`);
    console.log(`   Concept: ${thirdVideo.conceptMatchScore}% | Views: ${thirdVideo.viewCount.toLocaleString()} | Duration: ${Math.floor(thirdVideo.duration / 60)}m`);
    console.log(`   ID: ${thirdVideo.videoId}\n`);

    console.log(`═══════════════════════════════════════════════════════════\n`);

    const selectedVideos: SelectedVideos = {
      popular: firstVideo.videoId,
      conceptual: secondVideo.videoId,
      recent: thirdVideo.videoId
    };

    // Cache the first video (primary)
    CacheService.set(cacheKey, firstVideo.videoId, 1000 * 60 * 60 * 24 * 3);

    return { 
      videoId: firstVideo.videoId, 
      videos: selectedVideos,
      error: null 
    };

  } catch (error) {
    console.error('❌ Video search error:', error);
    return { videoId: null, error };
  }
}

/**
 * Extract YouTube video ID from URL or ID string
 */
export function extractVideoId(input: string): string | null {
  if (!input) return null;
  if (/^[a-zA-Z0-9_-]{11}$/.test(input)) return input;
  const match = input.match(/(?:v=|\/embed\/|youtu\.be\/|\/v\/|\/shorts\/)([a-zA-Z0-9_-]{11})/);
  return match ? match[1] : null;
}

